import React, { useCallback, useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import { api, btnPrimary, btnSecondary, card, InlineError, input, Modal, Skeleton, useToast } from '../ui/Kit';

// Organization → Job board integration (approved mockup JobBoards): a catalog from one config (backend
// job_board_catalog.py) — search, regions, capability chips, Connected / Available / Coming soon. Coming soon →
// "Request" (stored; the master admin is told). Dice / Monster / CareerBuilder keep their API-key connect: keys are
// write-only, the backend only ever returns the last 4 characters.
const STATUS = { connected: ['Connected', 'bg-[#DCFCE7] text-[#166534]'], invalid: ['Key rejected', 'bg-[#FEE2E2] text-[#991B1B]'],
  available: ['Available', 'bg-[#E0F2FE] text-[#075985]'], coming_soon: ['Coming soon', 'bg-[#F1F5F9] text-[#475569]'] };
const pill = 'inline-flex items-center px-[9px] py-[3px] rounded-full text-[11px] font-semibold whitespace-nowrap';
const chipCls = (on) => `inline-flex items-center min-h-[36px] px-3.5 rounded-full border text-[13px] font-semibold ${on ? 'bg-[#0F172A] border-[#0F172A] text-white' : 'bg-white border-[#D9DEE8] text-[#334155]'}`;

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function KeyDialog({ board, companyId, onClose, onDone }) {
  const [key, setKey] = useState('');
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  useEffect(() => {
    fetch(API_ENDPOINTS.INTEGRATIONS_JOB_BOARDS(companyId), { headers: auth() }).then((r) => r.json())
      .then((j) => setInfo((j.data || []).find((b) => b.board === board.key) || null)).catch(() => setInfo(null));
  }, [board.key, companyId]);
  const save = async () => {
    const apiKey = key.trim();
    if (apiKey.length < 8) { setError('API key looks too short'); return; }
    setBusy(true); setError('');
    try {
      const res = await fetch(API_ENDPOINTS.INTEGRATIONS_JOB_BOARD(companyId, board.key), {
        method: 'PUT', headers: { ...auth(), 'Content-Type': 'application/json' }, body: JSON.stringify({ api_key: apiKey }) });
      const json = await res.json();
      if (!res.ok) throw new Error(typeof json.detail === 'string' ? json.detail : 'Failed to save key');
      if (json.data.status === 'invalid') { setError(json.data.last_error || 'The board rejected this key'); setInfo(json.data); setBusy(false); onDone(false); return; }
      onDone(true, `${board.name} key saved`);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  const disconnect = async () => {
    setBusy(true); setError('');
    try {
      const res = await fetch(API_ENDPOINTS.INTEGRATIONS_JOB_BOARD(companyId, board.key), { method: 'DELETE', headers: auth() });
      const json = await res.json();
      if (!res.ok) throw new Error(typeof json.detail === 'string' ? json.detail : 'Failed to disconnect');
      onDone(true, `${board.name} disconnected`);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  const hasKey = info && (info.status === 'connected' || info.status === 'invalid');
  return (
    <Modal open title={`${board.name} — ${hasKey ? 'manage' : 'connect'}`} onClose={onClose}
      footer={<><button className={btnSecondary} onClick={onClose}>Close</button>
        {confirmOff ? <button className="px-3.5 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold" disabled={busy} onClick={disconnect}>Disconnect</button>
          : <button className={btnPrimary} disabled={busy || key.trim().length < 8} onClick={save}>{hasKey ? 'Replace key' : 'Connect'}</button>}</>}>
      {hasKey && (
        <div className="space-y-1 mb-3">
          <div className="font-mono text-sm bg-[#F8FAFC] border border-[#E6E9F0] rounded-lg px-3 py-2">••••••••{info.api_key_last4}</div>
          <p className="text-xs text-[#64748B]">Connected{info.connected_by ? ` by ${info.connected_by}` : ''}{info.connected_at ? ` on ${new Date(info.connected_at).toLocaleDateString()}` : ''}</p>
          {!info.provider_live && <p className="text-xs text-[#92400E] bg-[#FFFBEB] border border-[#FDE68A] rounded-lg px-2 py-1">Key saved — live data pending partner API approval</p>}
        </div>
      )}
      <input type="password" autoComplete="new-password" spellCheck={false} className={input} placeholder={`${board.name} API key`} aria-label={`${board.name} API key`}
        value={key} onChange={(e) => setKey(e.target.value)} />
      <p className="text-xs text-[#64748B] mt-1.5">Your company's own licence with {board.name}. The key is stored encrypted and never shown again.</p>
      {hasKey && !confirmOff && <button type="button" className="mt-3 text-sm font-semibold text-[#B91C1C]" onClick={() => setConfirmOff(true)}>Disconnect {board.name}…</button>}
      {confirmOff && <p className="mt-3 text-sm text-[#334155]">Disconnect {board.name}? The stored key is deleted and recruiters fall back to public data.</p>}
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

export default function JobBoardsTab({ companyId }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [region, setRegion] = useState('All');
  const [manage, setManage] = useState(null);
  const load = useCallback(() => api('/api/job-boards/catalog').then(setData).catch((e) => { setError(e.message); setData({ boards: [], sections: [], connected: 0 }); }), []);
  useEffect(() => { load(); }, [load]);
  const request = async (b) => {
    try { setData(await api(`/api/job-boards/${b.key}/request`, { method: 'POST' })); toast.show(`Requested ${b.name} — we'll let you know`); }
    catch (e) { toast.show(e.message, 'error'); }
  };
  if (!data) return <Skeleton className="h-64 w-full" />;
  const ql = q.trim().toLowerCase();
  const shown = data.boards.filter((b) => (!ql || `${b.name} ${b.regions} ${b.capabilities.join(' ')}`.toLowerCase().includes(ql))
    && (region === 'All' || (region === 'Connected' ? b.status === 'connected' : b.section === region)));
  return (
    <div className="space-y-[18px]" data-testid="job-boards">
      <div className="flex gap-3 flex-wrap items-center">
        <input aria-label="Search job boards" className={input + ' flex-1 min-w-[240px] !min-h-[40px]'} placeholder={`Search ${data.boards.length} job boards…`} value={q} onChange={(e) => setQ(e.target.value)} />
        <nav aria-label="Regions" className="flex gap-2 flex-wrap">
          {['All', ...data.sections].map((r) => <button key={r} type="button" aria-pressed={region === r} className={chipCls(region === r)} onClick={() => setRegion(r)}>{r}</button>)}
          <button type="button" aria-pressed={region === 'Connected'} className={chipCls(region === 'Connected')} onClick={() => setRegion('Connected')}>Connected · {data.connected}</button>
        </nav>
      </div>
      <InlineError>{error}</InlineError>
      <div className={card + ' rounded-[16px] px-[18px] py-3.5 flex gap-3.5 items-center bg-[#F5F9FF] border-[#CFE2F7]'}>
        <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0066CC" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>
        <p className="flex-1 text-[13.5px] m-0"><b>Market data</b> (salary checks during the Jessica call) uses O*NET + BLS public data — free and always on. Boards marked <b>Resume search</b> use your company’s own licence with that board.</p>
      </div>
      {!shown.length && <p className="text-sm text-[#64748B]">No job boards match.</p>}
      {data.sections.map((sec) => {
        const boards = shown.filter((b) => b.section === sec);
        if (!boards.length) return null;
        return (
          <section key={sec} aria-label={sec} className="flex flex-col gap-3">
            <h2 className="text-[17px] font-bold m-0">{sec}</h2>
            <div className="grid gap-3.5 grid-cols-[repeat(auto-fill,minmax(260px,1fr))]">
              {boards.map((b) => {
                const [label, cls] = STATUS[b.status] || STATUS.coming_soon;
                return (
                  <div key={b.key} className={card + ` rounded-[16px] px-[18px] py-4 flex flex-col gap-2.5 ${b.status === 'connected' ? '!border-[#86EFAC]' : ''}`} data-testid={`board-${b.key}`}>
                    <div className="flex gap-3 items-center">
                      <span className="w-10 h-10 rounded-[10px] shrink-0 flex items-center justify-center font-extrabold text-sm" style={{ background: b.logo.bg, color: b.logo.fg }} aria-hidden="true">{b.logo.text}</span>
                      <div className="flex-1 min-w-0"><p className="font-bold">{b.name}</p><p className="text-xs text-[#64748B]">{b.regions}</p></div>
                      <span className={`${pill} ${cls}`}>{label}</span>
                    </div>
                    <div className="flex gap-1.5 flex-wrap">{b.capabilities.map((c) => <span key={c} className="inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold bg-[#F1F5F9] text-[#334155]">{c}</span>)}</div>
                    {b.requested && <p className="text-xs text-[#64748B]">Requested by {b.requested_by || '1 person'}</p>}
                    {b.connector ? (
                      <button type="button" className={b.status === 'available' ? btnPrimary + ' justify-center' : btnSecondary + ' justify-center'} onClick={() => setManage(b)}>
                        {b.status === 'available' ? 'Connect' : 'Manage'}</button>
                    ) : b.requested ? (
                      <button type="button" className={btnSecondary + ' justify-center'} disabled>Requested ✓</button>
                    ) : (
                      <button type="button" className={btnSecondary + ' justify-center'} onClick={() => request(b)} aria-label={`Request ${b.name}`}>Request</button>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
      {manage && <KeyDialog board={manage} companyId={companyId} onClose={() => setManage(null)}
        onDone={(close, msg) => { load(); if (close) { setManage(null); if (msg) toast.show(msg); } }} />}
    </div>
  );
}
